import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SelectableCard } from '../components/onboarding/SelectableCard';
import { SetupScaffold } from '../components/onboarding/SetupScaffold';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import {
  SETUP_CATEGORIES,
  SETUP_CATEGORIES_KEY,
  SETUP_ROLE_KEY,
  isMultiSelectRole,
} from '../components/onboarding/accountSetup';

/**
 * Cuestionario post-registro, paso 2: rubro / categoría de la marca.
 * Réplica del frame de Figma (node 105:360) reutilizando tokens del DS v3.
 *
 * - **Multi o single-select según el rol del paso 1** (ADR-713): community manager
 *   o agencia → pueden elegir 1 o más; dueño o empleado → sólo 1.
 * - La lista **scrollea por detrás** de un botón "Siguiente" fijo abajo, con un
 *   gradiente + `backdrop-blur` en la parte inferior.
 * - "Siguiente" queda deshabilitado hasta elegir al menos una categoría; al
 *   continuar guarda la selección y va al paso 3 (`/configuracion-cuenta/contenido`).
 */
export const AccountSetupCategory = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string[]>([]);
  const [multi, setMulti] = useState(false);

  // Necesita sesión; además lee el rol del paso 1 para decidir multi/single.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && !data.session) navigate(ONBOARDING_V2.bienvenida, { replace: true });
    });
    let role: string | null = null;
    try {
      role = localStorage.getItem(SETUP_ROLE_KEY);
    } catch {}
    setMulti(isMultiSelectRole(role));
    return () => {
      active = false;
    };
  }, [navigate]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      if (!multi) return [id]; // single: reemplaza la anterior
      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    });
  };

  const handleNext = () => {
    if (selected.length === 0) return;
    try {
      localStorage.setItem(SETUP_CATEGORIES_KEY, JSON.stringify(selected));
    } catch {}
    navigate(ONBOARDING_V2.setupContenido);
  };

  return (
    <SetupScaffold
      footer={
        <button
          type="button"
          onClick={handleNext}
          disabled={selected.length === 0}
          className="flex w-full items-center justify-center rounded-[12px] bg-[#18181B] p-4 text-[16px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] disabled:cursor-not-allowed disabled:bg-[#9F9FA9] md:w-auto md:px-10"
        >
          Siguiente
        </button>
      }
    >
      <h1 className="text-ds-heading-xl text-[#18181B] md:text-center">
        Ahora, contanos sobre tu marca ¿A qué se dedica?
      </h1>

      <div
        className="grid grid-cols-2 gap-4 md:grid-cols-3"
        role={multi ? 'group' : 'radiogroup'}
        aria-label="Elegí el rubro de tu marca"
      >
        {SETUP_CATEGORIES.map((c) => (
          <SelectableCard
            key={c.id}
            label={c.label}
            image={c.image}
            selected={selected.includes(c.id)}
            onClick={() => toggle(c.id)}
            role={multi ? 'checkbox' : 'radio'}
          />
        ))}
      </div>
    </SetupScaffold>
  );
};

export default AccountSetupCategory;
